import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../icons';
import { BRAND, BRAND_LIGHT, getThemeColors } from '../../lib/theme';
import { useIsMobile } from '../../lib/useBreakpoint';
import { usePermissions } from '../../contexts/PermissionsContext';
import { pbList } from '../../lib/pbList';
import { buildPath, navigateGlobal, PAGE_IDS, type PageId } from '../../lib/route';
import { useMyDepartments, type MyDepartment } from '../../lib/departments';
import { msgraphApi } from '../../lib/msgraph';
import type { ThemeColors } from '../../types';

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

type Page = string;

interface NavItem {
  id: Page;
  label: string;
  icon: keyof typeof Icon;
  group: 'menu' | 'konto';
  badge?: number;
  /** If set, item is only shown when the admin has this permission. */
  permission?: string;
}

// i18n keys for nav labels — resolved at render time via useTranslation.
// Seit den Abteilungen (04.11.2026) sind Pflegebedürftige und Betreuungskräfte
// Hauptpunkte; Workflow/Dashboard/Archiv hängen je Abteilung in eigenen Blöcken.
const NAV: NavItem[] = [
  { id: 'dashboard',     label: 'nav.menu.dashboard', icon: 'Dashboard', group: 'menu' },
  { id: 'pflege',        label: 'nav.menu.pflege',    icon: 'Heart',     group: 'menu' },
  { id: 'betreuung',     label: 'nav.menu.betreuung', icon: 'Users',     group: 'menu' },
  { id: 'matching',      label: 'nav.menu.matching',  icon: 'Heart',     group: 'menu' },
  { id: 'chat',          label: 'nav.menu.chat',      icon: 'Chat',      group: 'menu' },
  { id: 'email',         label: 'nav.menu.email',     icon: 'Mail',      group: 'menu' },
  { id: 'kalender',      label: 'nav.menu.kalender',  icon: 'Calendar',  group: 'menu' },
  { id: 'benutzer',      label: 'nav.account.benutzer',      icon: 'UserCog', group: 'konto', permission: 'menu.benutzer' },
  { id: 'profil',        label: 'nav.account.profil',        icon: 'Users',   group: 'konto' },
  { id: 'einstellungen', label: 'nav.account.einstellungen', icon: 'Settings', group: 'konto', permission: 'menu.einstellungen' },
  { id: 'admin_einstellungen', label: 'nav.account.admin_einstellungen', icon: 'Shield', group: 'konto', permission: 'menu.admin_einstellungen' },
];

/** Einträge eines Abteilungsblocks. `beendet` nur für pb/bk, mit Permission. */
interface DeptEntry {
  tab: 'dashboard' | 'workflow' | 'beendet';
  label: string;
  icon: keyof typeof Icon;
  permission?: string;
}

/** Alte Seiten (Deep-Links) gehören zum Workflow bzw. Archiv der pb-/bk-Abteilung. */
function legacyTarget(page: string): { pipeline: 'pb' | 'bk'; tab: DeptEntry['tab'] } | null {
  switch (page) {
    case 'anfragen': return { pipeline: 'pb', tab: 'workflow' };
    case 'anfragen_bk': return { pipeline: 'bk', tab: 'workflow' };
    case 'anfragen_beendet': return { pipeline: 'pb', tab: 'beendet' };
    case 'anfragen_bk_beendet': return { pipeline: 'bk', tab: 'beendet' };
    default: return null;
  }
}

function deptEntries(d: MyDepartment): DeptEntry[] {
  const entries: DeptEntry[] = [
    { tab: 'dashboard', label: 'nav.menu.dashboard',     icon: 'Dashboard' },
    { tab: 'workflow',  label: 'nav.menu.workflow',      icon: 'Inbox' },
  ];
  if (d.pipeline === 'pb') {
    entries.push({ tab: 'beendet', label: 'nav.menu.anfragen_beendet_sub', icon: 'Archive', permission: 'menu.beendete_anfragen' });
  } else if (d.pipeline === 'bk') {
    entries.push({ tab: 'beendet', label: 'nav.menu.anfragen_beendet_sub', icon: 'Archive', permission: 'menu.beendete_anfragen_bk' });
  }
  return entries;
}

function defaultOpenDepartment(slug: string, tab: string): void {
  navigateGlobal('abteilung', slug, tab);
}

interface SidebarProps {
  page: Page;
  setPage: (page: Page) => void;
  dark: boolean;
  collapsed: boolean;
  setCollapsed: (v: boolean | ((p: boolean) => boolean)) => void;
  /** Off-canvas open state (only consulted when the viewport is mobile). */
  mobileOpen?: boolean;
  /** Called when the user dismisses the mobile drawer (overlay tap or nav-click). */
  onMobileClose?: () => void;
  /** Route-Segmente der Abteilungsseite (/abteilung/<slug>/<tab>) für die Markierung. */
  routeSub?: string | null;
  routeTab?: string | null;
  /** Öffnet einen Eintrag eines Abteilungsblocks (Standard: globale Navigation per History). */
  onOpenDepartment?: (slug: string, tab: string) => void;
}

export function Sidebar({
  page,
  setPage,
  dark,
  collapsed,
  setCollapsed,
  mobileOpen = false,
  onMobileClose,
  routeSub = null,
  routeTab = null,
  onOpenDepartment,
}: SidebarProps) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const isMobile = useIsMobile();
  // Collapsed mode only makes sense on desktop. On mobile the drawer is
  // always fully expanded; visibility is controlled by mobileOpen.
  const effectiveCollapsed = isMobile ? false : collapsed;
  const width = effectiveCollapsed ? 76 : (isMobile ? 280 : 248);

  const handleNavClick = (id: Page) => {
    setPage(id);
    if (isMobile && onMobileClose) onMobileClose();
  };

  if (isMobile) {
    // ── Off-canvas drawer ──
    return (
      <>
        <div
          onClick={onMobileClose}
          aria-hidden={!mobileOpen}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(16,24,40,0.5)',
            opacity: mobileOpen ? 1 : 0,
            pointerEvents: mobileOpen ? 'auto' : 'none',
            transition: 'opacity 0.2s ease',
            zIndex: 50,
          }}
        />
        <aside style={{
          position: 'fixed', top: 0, bottom: 0, left: 0,
          width, maxWidth: '85vw',
          borderRight: `1px solid ${t.border}`,
          background: t.card,
          display: 'flex', flexDirection: 'column',
          padding: '22px 16px 16px',
          transform: `translateX(${mobileOpen ? '0' : '-100%'})`,
          transition: 'transform 0.25s ease',
          zIndex: 51,
          overflowX: 'hidden',
          overflowY: 'auto',
          overscrollBehavior: 'contain',
        }}>
          <SidebarBody
            t={t}
            tr={tr}
            page={page}
            setPage={handleNavClick}
            collapsed={false}
            setCollapsed={null}
            dark={dark}
            routeSub={routeSub}
            routeTab={routeTab}
            onDeptNavigate={(slug, tab) => {
              (onOpenDepartment ?? defaultOpenDepartment)(slug, tab);
              if (onMobileClose) onMobileClose();
            }}
          />
        </aside>
      </>
    );
  }

  // ── Desktop in-flow sidebar ──
  return (
    <aside style={{
      width, flexShrink: 0,
      borderRight: `1px solid ${t.border}`,
      background: t.card,
      display: 'flex', flexDirection: 'column',
      padding: effectiveCollapsed ? '22px 12px 16px' : '22px 16px 16px',
      transition: 'width 0.25s ease',
      // Vertikal scrollbar, damit bei vielen Abteilungsblöcken / kleiner
      // Fensterhöhe alle Menüpunkte erreichbar bleiben. Horizontal bleibt
      // abgeschnitten (Breitenanimation beim Ein-/Ausklappen).
      minHeight: 0,
      overflowX: 'hidden',
      overflowY: 'auto',
      overscrollBehavior: 'contain',
    }} data-testid="sidebar-desktop">
      <SidebarBody
        t={t}
        tr={tr}
        page={page}
        setPage={handleNavClick}
        collapsed={effectiveCollapsed}
        setCollapsed={setCollapsed}
        dark={dark}
        routeSub={routeSub}
        routeTab={routeTab}
        onDeptNavigate={(slug, tab) => (onOpenDepartment ?? defaultOpenDepartment)(slug, tab)}
      />
    </aside>
  );
}

// ─── Shared inner content used by both mobile drawer and desktop column ────

interface SidebarBodyProps {
  t: ThemeColors;
  tr: TranslateFn;
  page: Page;
  setPage: (id: Page) => void;
  collapsed: boolean;
  /** When null, the collapse-toggle button is omitted (mobile drawer). */
  setCollapsed: ((v: boolean | ((p: boolean) => boolean)) => void) | null;
  dark: boolean;
  routeSub: string | null;
  routeTab: string | null;
  /** Klick in einem Abteilungsblock (auf Mobile zusätzlich: Drawer schließen). */
  onDeptNavigate: (slug: string, tab: string) => void;
}

function SidebarBody({ t, tr, page, setPage, collapsed, setCollapsed, dark, routeSub, routeTab, onDeptNavigate }: SidebarBodyProps) {
  const { hasPermission } = usePermissions();
  const { departments, refresh: refreshDepartments } = useMyDepartments();

  // Live-Badges: Anfragen = Anzahl offener PB-Anfragen, exakt wie die Liste
  // unter /anfragen (scope=anfragen, ohne abgebrochene). Früher kam hier
  // `new_users` aus dem v1-badges-Endpoint (alle konto=2-User inkl. BKs) —
  // das zeigte „99+", obwohl keine Anfrage offen war.
  // E-Mail = ungelesene Mails im verbundenen Microsoft-Postfach.
  const [liveBadges, setLiveBadges] = useState<Record<string, number>>({});
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await pbList.list('anfragen', { page: 1, perPage: 1, filters: { excludeCancelled: 1 } });
        if (alive) {
          setLiveBadges(prev => {
            const next = { ...prev };
            if (res.total > 0) next.anfragen = res.total;
            else delete next.anfragen;
            return next;
          });
        }
      } catch { /* silent */ }
      try {
        const unread = await msgraphApi.unread();
        if (alive) {
          setLiveBadges(prev => {
            const next = { ...prev };
            if (unread != null && unread > 0) next.email = unread;
            else delete next.email;
            return next;
          });
        }
      } catch { /* silent */ }
      // WP4: Benachrichtigungs-Polling lebt im Header (Glocke + Toast +
      // Browser-Notification) — hier bewusst keine zweite Quelle.
    };
    void load();
    const timer = window.setInterval(() => { void load(); refreshDepartments(); }, 60000);
    return () => { alive = false; clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Items, für die dem Admin die Permission fehlt, ausblenden; Live-Badges einmischen.
  const visibleNav = NAV
    .filter(node => !node.permission || hasPermission(node.permission))
    .map(node => ({ ...node, badge: liveBadges[node.id] }));

  // Badge je Abteilung: für die PB-Abteilung (VBS) die bisherige Anfragen-Zahl
  // (identisch zur Liste unter /anfragen), sonst workflow_count aus /me/departments.
  const legacy = legacyTarget(page);
  const deptBadge = (d: MyDepartment): number | undefined => {
    const n = d.pipeline === 'pb' ? liveBadges.anfragen : d.workflow_count;
    return n != null && n > 0 ? n : undefined;
  };
  return (
    <>
      {/* Logo + collapse button */}
      <div style={{
        display: 'flex', alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'space-between',
        marginBottom: 26, minHeight: 40,
      }}>
        {!collapsed ? (
          <>
            <img
              src={dark ? '/assets/senihelp24-logo-white.png' : '/assets/senihelp24-logo-color.png'}
              alt="Senihelp24"
              style={{ height: 36, objectFit: 'contain', maxWidth: 150 }}
            />
            {setCollapsed && (
              <button
                onClick={() => setCollapsed(true)}
                style={{
                  width: 28, height: 28, borderRadius: 7,
                  border: `1px solid ${t.border}`, background: 'transparent',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <Icon.ChevronLR dir="left" c={t.muted}/>
              </button>
            )}
          </>
        ) : (
          setCollapsed && (
            <button
              onClick={() => setCollapsed(false)}
              style={{
                width: 40, height: 40, borderRadius: 9,
                border: 'none', background: BRAND, color: '#fff',
                fontWeight: 700, fontSize: 16, cursor: 'pointer',
                fontFamily: 'Outfit, sans-serif',
              }}
            >
              S
            </button>
          )
        )}
      </div>

      {/* Menu section */}
      {!collapsed && (
        <div style={{
          fontSize: 10, fontWeight: 600, color: t.placeholder,
          textTransform: 'uppercase', letterSpacing: '0.08em',
          marginBottom: 8, paddingLeft: 4,
        }}>
          {tr('nav.menu.title')}
        </div>
      )}
      <nav style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 20 }}>
        {visibleNav.filter(i => i.group === 'menu').map(it => (
          <NavItem key={it.id} it={it} active={page === it.id} collapsed={collapsed} dark={dark} onClick={() => setPage(it.id)}/>
        ))}
      </nav>

      {/* Abteilungsblöcke: je Abteilung „Abteilung <Name>" mit Dashboard, Workflow (und Archiv). */}
      {departments.map(d => {
        const entries = deptEntries(d).filter(e => !e.permission || hasPermission(e.permission));
        return (
          <div key={d.slug} data-testid={`dept-block-${d.slug}`} style={{ marginBottom: 20 }}>
            {!collapsed ? (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6,
                fontSize: 10, fontWeight: 600, color: t.placeholder,
                textTransform: 'uppercase', letterSpacing: '0.08em',
                marginBottom: 8, paddingLeft: 4,
              }}>
                <span aria-hidden style={{ width: 8, height: 8, borderRadius: 2, background: d.color, flexShrink: 0 }}/>
                <span>{tr('nav.menu.department', { name: d.name })}</span>
              </div>
            ) : (
              <div aria-hidden style={{ height: 3, margin: '0 12px 8px', borderRadius: 2, background: d.color }}/>
            )}
            <nav style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {entries.map(e => {
                const badge = e.tab === 'workflow' ? deptBadge(d) : undefined;
                const label = tr(e.label);
                return (
                  <NavItem
                    key={e.tab}
                    it={{ id: `abteilung:${d.slug}:${e.tab}`, label: e.label, icon: e.icon, group: 'menu', badge }}
                    active={
                      (page === 'abteilung' && routeSub === d.slug && (routeTab ?? 'dashboard') === e.tab)
                      || (legacy != null && legacy.pipeline === d.pipeline && legacy.tab === e.tab)
                    }
                    collapsed={collapsed}
                    dark={dark}
                    href={buildPath('abteilung', d.slug, e.tab)}
                    title={`${d.name}: ${label}`}
                    onClick={() => onDeptNavigate(d.slug, e.tab)}
                  />
                );
              })}
            </nav>
          </div>
        );
      })}

      {!collapsed && (
        <div style={{
          fontSize: 10, fontWeight: 600, color: t.placeholder,
          textTransform: 'uppercase', letterSpacing: '0.08em',
          marginBottom: 8, paddingLeft: 4,
        }}>
          {tr('nav.account.title')}
        </div>
      )}
      <nav style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {visibleNav.filter(i => i.group === 'konto').map(it => (
          <NavItem key={it.id} it={it} active={page === it.id} collapsed={collapsed} dark={dark} onClick={() => setPage(it.id)}/>
        ))}
      </nav>

      {/* Footer */}
      <div style={{ marginTop: 'auto' }}>
        {!collapsed && (
          <div style={{
            background: t.cardMuted, border: `1px solid ${t.border}`,
            borderRadius: 12, padding: 14, textAlign: 'center',
          }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: t.heading }}>Senihelp24 CRM 2.0</div>
          </div>
        )}
      </div>
    </>
  );
}

interface NavItemProps {
  it: NavItem;
  active: boolean;
  collapsed: boolean;
  dark: boolean;
  onClick: () => void;
  /** Expliziter Link-Pfad (Abteilungsblöcke); sonst aus der Seiten-ID abgeleitet. */
  href?: string;
  /** Tooltip, wenn die Sidebar eingeklappt ist (Standard: Label). */
  title?: string;
}

/**
 * Menüpunkt als echter Link.
 *
 * Gerendert wird ein `<a href>` statt eines `<button>`, damit die Seite über
 * Mittelklick / Strg+Klick / „Link in neuem Tab öffnen" in einem zweiten Tab
 * landen kann und die Adresse kopierbar ist. Der normale Linksklick bleibt
 * SPA-Navigation (kein Reload): `preventDefault()` läuft nur für den
 * unmodifizierten Primärklick — bei Strg/Cmd/Shift/Alt oder Mittelklick
 * übernimmt der Browser, sonst wäre genau das kaputt, was der Link bringt.
 *
 */
function navHref(id: string): string | undefined {
  return (PAGE_IDS as string[]).includes(id) ? buildPath(id as PageId) : undefined;
}

/** Der Browser soll den Klick selbst behandeln (neuer Tab/Fenster, Download …). */
function isModifiedClick(e: React.MouseEvent): boolean {
  return e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0;
}

function NavItem({ it, active, collapsed, dark, onClick, href: hrefProp, title: titleProp }: NavItemProps) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const IconComp = Icon[it.icon] as React.ComponentType<{ c: string }>;
  // Resolve i18n key to translated label; fall back to raw key if translation missing
  const label = tr(it.label);
  const href = hrefProp ?? navHref(it.id);
  const tooltip = collapsed ? (titleProp ?? label) : '';

  const handleMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
    if (!active) e.currentTarget.style.background = t.hover;
  };
  const handleMouseLeave = (e: React.MouseEvent<HTMLElement>) => {
    if (!active) e.currentTarget.style.background = 'transparent';
  };

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isModifiedClick(e)) return; // neuer Tab/Fenster — Browser übernimmt
    e.preventDefault();
    onClick();
  };

  const style: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 12,
    borderRadius: 8, padding: collapsed ? '9px' : '9px 11px',
    border: 'none',
    background: active ? (dark ? '#2a1222' : BRAND_LIGHT) : 'transparent',
    color: active ? BRAND : t.text,
    fontSize: 13, fontWeight: 500, cursor: 'pointer', textAlign: 'left',
    justifyContent: collapsed ? 'center' : 'flex-start',
    position: 'relative', transition: 'background 0.15s',
    width: '100%', boxSizing: 'border-box',
    fontFamily: 'Outfit, sans-serif',
    textDecoration: 'none',
  };

  const inner = (
    <>
      <IconComp c={active ? BRAND : t.muted}/>
      {!collapsed && <span style={{ flex: 1 }}>{label}</span>}
      {!collapsed && it.badge && (
        <span style={{
          background: BRAND, color: '#fff',
          borderRadius: 9999, padding: '1px 7px',
          fontSize: 10, fontWeight: 600,
        }}>
          {it.badge > 99 ? '99+' : it.badge}
        </span>
      )}
      {collapsed && it.badge && (
        <span style={{
          position: 'absolute', top: 4, right: 6,
          width: 8, height: 8, borderRadius: '50%',
          background: BRAND, border: `2px solid ${t.card}`,
        }}/>
      )}
    </>
  );

  // Kein bekannter Page-Pfad → Button-Fallback statt totem Link. Greift nur
  // für Einträge, die nicht in PAGE_IDS stehen; ein ausgelieferter Menüpunkt
  // gehört dort hinein (siehe route.ts), sonst ist er nicht verlinkbar.
  if (!href) {
    return (
      <button
        onClick={onClick}
        title={tooltip}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={style}
      >
        {inner}
      </button>
    );
  }

  return (
    <a
      href={href}
      onClick={handleClick}
      title={tooltip}
      aria-current={active ? 'page' : undefined}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={style}
    >
      {inner}
    </a>
  );
}
